import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';

import ProformaInvoiceEntity, {
  ProformaInvoiceItemEntity,
  PROFORMA_INVOICE_STATUS,
  PAID_STATUS_SOURCE,
} from './ProformaInvoiceEntity';
import ProformaInvoiceDC, { ProformaInvoiceItemDC } from './ProformaInvoiceDC';
import {
  mapProposalSettingsDСtoEntity,
  mapProposalSettingsEntityToDC,
} from '../proposalSettings/mapper';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import { ProformaInvoiceUpsertPayload } from 'src/api/proformaInvoices/types';

const normalizeItems = (items: ProformaInvoiceDC['items']): ProformaInvoiceItemEntity[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return [...items]
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      order: item.order,
      description: item.description ?? null,
      qty: item.qty,
      amount: item.amount,
    }));
};

const toMillis = (value: number | null): number | null => (value != null ? value * 1000 : null);

export const mapProformaInvoiceDCToEntity = (
  dc: ProformaInvoiceDC,
  workspaceId: string
): ProformaInvoiceEntity => {
  const status = dc.paid_status ?? PROFORMA_INVOICE_STATUS.PENDING_PAYMENT;

  return {
    uuid: dc.uuid,
    workspaceId: dc.workspace_id ?? workspaceId,
    invoiceId: dc.invoice_id ?? null,
    priceProposalId: dc.price_proposal_id ?? null,
    number: dc.number ?? null,
    priceProposalReference: dc.price_proposal_reference ?? null,
    contactName: dc.contact_name ?? null,
    contactId: dc.contact_id ?? null,
    title: dc.title ?? null,
    subtotalPrice: dc.subtotal_price ?? null,
    priceWithDiscount: dc.price_with_discount ?? null,
    taxValue: dc.tax_value ?? null,
    priceWithTax: dc.price_with_tax ?? null,
    description: dc.description ?? null,
    discountType: dc.discount_type ?? null,
    discountAmount: dc.discount_amount ?? null,
    items: normalizeItems(dc.items),
    settings: dc.settings ? mapProposalSettingsDСtoEntity(dc.settings) : null,
    status,
    createdAt: toMillis(dc.created_at),
    updatedAt: toMillis(dc.updated_at),
    dueDate: toMillis(dc.due_date),
    createdBy: dc.created_by ?? null,
    updatedBy: dc.updated_by ?? null,
    priceProposalOverrideFields: dc.override_pp_fields ?? false,
    priceProposalOverrideSettings: dc.override_settings ?? false,
    reviewRequiredAt: toMillis(dc.review_required_at ?? null),
    paidStatusSource: dc.paid_status_source ?? PAID_STATUS_SOURCE.MANUAL,
    totalPaid: dc.total_paid ?? 0,
    balanceDue: dc.balance_due ?? null,
  };
};

const normalizeDraftItems = (items: ProformaInvoiceDraft['items']): ProformaInvoiceItemDC[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item, index) => ({
    order: item.order ?? index + 1,
    description: item.description ?? null,
    qty: item.qty,
    amount: item.amount,
  }));
};

/**
 * Fields the user edits in the form. Totals, number, invoice id, quote reference
 * and authorship are left out: the backend computes or assigns them.
 */
const mapDraftToEditableFields = (draft: ProformaInvoiceDraft): ProformaInvoiceUpsertPayload => ({
  price_proposal_id: draft.priceProposalId || null,
  contact_id: draft.contactId ?? null,
  contact_name: draft.contactName ?? null,
  title: draft.title ?? null,
  description: draft.description ?? null,
  discount_type: draft.discountType ?? null,
  discount_amount: draft.discountAmount ?? null,
  items: normalizeDraftItems(draft.items),
  settings: draft.settings ? mapProposalSettingsEntityToDC(draft.settings) : null,
  due_date: draft.dueDate ? Math.floor(draft.dueDate / 1000) : null,
  paid_status: draft.status ?? null,
});

export const mapProformaInvoiceDraftToCreatePayload = (
  draft: ProformaInvoiceDraft
): ProformaInvoiceUpsertPayload => {
  const payload: ProformaInvoiceUpsertPayload = {
    uuid: draft.uuid,
    ...mapDraftToEditableFields(draft),
    updated_by: draft.updatedBy ?? null,
  };

  if (payload.price_proposal_id) {
    payload.override_pp_fields = draft.priceProposalOverrideFields;
    payload.override_settings = draft.priceProposalOverrideSettings;
    // The backend prefers settings from the payload over the quote's settings.
    if (draft.priceProposalOverrideSettings) {
      delete payload.settings;
    }
  }

  return payload;
};

/**
 * PATCH body with only the fields that differ from `initialDraft`, the values
 * the form started from. Unchanged fields are not sent, so the update never
 * overwrites data the user did not touch (e.g. totals of invoices without
 * items, or settings the backend creates lazily for older invoices).
 * `updated_at` is not sent either: the backend stamps the save itself, so a
 * client clock running behind cannot make the save be ignored.
 */
export const mapProformaInvoiceDraftToUpdatePayload = (
  draft: ProformaInvoiceDraft,
  initialDraft: ProformaInvoiceDraft
): ProformaInvoiceUpsertPayload => {
  const next = mapDraftToEditableFields(draft);
  const initial = mapDraftToEditableFields(initialDraft);

  const payload: ProformaInvoiceUpsertPayload = {
    uuid: draft.uuid,
    ...pickBy(next, (value, key) => !isEqual(value, initial[key])),
    updated_by: draft.updatedBy ?? null,
  };

  // The backend applies the override flags only when the linked quote changes.
  if (payload.price_proposal_id) {
    payload.override_pp_fields = draft.priceProposalOverrideFields;
    payload.override_settings = draft.priceProposalOverrideSettings;
    // Sent settings would be written over the settings copied from the quote.
    if (draft.priceProposalOverrideSettings) {
      delete payload.settings;
    }
  }

  return payload;
};
